import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { setTripInfo, setTripReference } from "../../Reducers/tripSlice";
import DriverPage from './DriverPage';
import { Helmet } from 'react-helmet';
import { FiMapPin, FiInfo, FiPhone } from 'react-icons/fi';
import { FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConfirmModal from './ConfirmModal';
import { fetchTripDetails, updateTripStatus } from '../../Services/ApiCallsServices/DriverDashboardApi';
import PulseLoader from 'react-spinners/PulseLoader';
import { useNavigate } from 'react-router-dom';

const TripProgress = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tripReference = useSelector(state => state.trip.tripReference);
  const tripInfo = useSelector(state => state.trip.tripInfo);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const statuses = ['Pending', 'Driving to Pickup', 'Driving to Destination', 'Completed', 'Cancelled'];

  useEffect(() => {
    if (!tripReference) {
      setError('Trip reference is missing');
      return;
    }

    const loadTripDetails = async () => {
      try {
        setIsLoading(true);
        const data = await fetchTripDetails(tripReference);
        dispatch(setTripInfo(data));
        dispatch(setTripReference(tripReference));
        setStatus(data.status);
      } catch (error) {
        setError('Error fetching trip details.');
        toast.error('Error fetching trip details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTripDetails();
  }, [tripReference, dispatch]);

  const openModal = (newStatus) => {
    setSelectedStatus(newStatus);
    setModalOpen(true);
  };

  const handleStatusChange = async () => {
    setModalOpen(false);
    try {
      setIsLoading(true);
      const data = await updateTripStatus(tripReference, selectedStatus);
      setStatus(selectedStatus);
      toast.success(data.message || 'Trip status updated successfully!');

      if (selectedStatus === 'Cancelled') {
        navigate(`/driver/cancel-reason/${tripReference}`);
      } else if (selectedStatus === 'Completed') {
        navigate("/driver/trip-completed");
      }
    } catch (error) {
      toast.error(error.message || 'Error updating status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = () => {
    switch(status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'driving to pickup':
      case 'driving to destination':
      case 'in progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAvailableStatuses = () => {
    const currentIndex = statuses.indexOf(status);
    if (currentIndex === -1 || status === 'Completed' || status === 'Pending') {
      return [status];
    }
    return statuses.slice(currentIndex);
  };

  const availableStatuses = getAvailableStatuses();

  if (error) {
    return (
        <DriverPage>
          <Helmet><title>Trip Progress | Error</title></Helmet>
          <div className="flex justify-center items-center h-screen text-red-500">
            {error}
          </div>
        </DriverPage>
    );
  }

  if (isLoading && !tripInfo) {
    return (
        <DriverPage>
          <Helmet><title>Trip Progress</title></Helmet>
          <div className="flex justify-center items-center h-64">
            <PulseLoader color="#FFD700" size={15} />
          </div>
        </DriverPage>
    );
  }

  return (
      <DriverPage>
        <Helmet><title>Trip Progress</title></Helmet>
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Heading + subtitle */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                <span className="text-yellow-500">Trip</span> Progress
              </h1>
              <p className="text-gray-600">Real-time updates on your delivery progress</p>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
              {/* Current Status Banner */}
              <div className={`px-6 py-4 ${getStatusColor()} flex items-center mb-6`}>
                <FiInfo className="mr-2" />
                <span className="font-medium">Current Status:</span>
                <span className="ml-2 font-semibold capitalize">{status}</span>
              </div>

              {/* Trip Details */}
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2 border-yellow-500">
                  Trip Details
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <FaUser className="text-yellow-500" />
                      <div>
                        <p className="text-sm text-gray-500">Customer Name</p>
                        <p className="font-medium">{tripInfo?.customerName || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <FiPhone className="text-yellow-500" />
                      <div>
                        <p className="text-sm text-gray-500">Customer Number</p>
                        <p className="font-medium">{tripInfo?.customerNumber || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Call Button */}
                  <a
                      href={`tel:${tripInfo?.customerNumber}`}
                      className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
                  >
                    <FiPhone className="mr-2" />
                    Call Customer
                  </a>

                  <div className="flex items-center space-x-3 mt-6">
                    <FiInfo className="text-yellow-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Trip Status</p>
                      {status === 'Completed' || status === 'Pending' ? (
                          <span className={`py-1 rounded text-sm ${
                              status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                        {status}
                      </span>
                      ) : (
                          <select
                              value={status}
                              onChange={(e) => openModal(e.target.value)}
                              className="bg-gray-200 block w-full pr-10 py-1 text-base border-gray-500 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm rounded-md"
                          >
                            {availableStatuses.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                      )}
                    </div>
                  </div>

                  {/* Trip Locations Section */}
                  <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2 border-yellow-500">
                    Trip Locations
                  </h2>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <FiMapPin className="text-green-500 mr-2" />
                        <span className="font-medium text-gray-700">Pickup Location</span>
                      </div>
                      <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tripInfo?.pickupLocation)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-900 hover:underline"
                      >
                        {tripInfo?.pickupLocation || 'N/A'}
                      </a>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <FiMapPin className="text-red-500 mr-2" />
                        <span className="font-medium text-gray-700">Dropoff Location</span>
                      </div>
                      <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tripInfo?.dropoffLocation)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-900 hover:underline"
                      >
                        {tripInfo?.dropoffLocation || 'N/A'}
                      </a>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <ConfirmModal
              isOpen={modalOpen}
              onRequestClose={() => setModalOpen(false)}
              onClose={() => setModalOpen(false)}
              title="Confirm Status Change"
              message={`Are you sure you want to change status to: ${selectedStatus}?`}
              onConfirm={handleStatusChange}
          />
        </div>
      </DriverPage>
  );
};

export default TripProgress;
